import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [previewToken, setPreviewToken] = useState("");

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data.reset_token) {
        setPreviewToken(data.reset_token);
      } else {
        toast.success("If that account exists, a reset link was created.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-8">
      <button data-testid="forgot-back-btn" onClick={() => nav("/login")} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10">
        <ArrowLeft size={18} />
      </button>
      <div className="mt-10">
        <h1 className="font-display text-4xl tracking-tight text-white">Reset password</h1>
        <p className="mt-2 text-neutral-400">Enter your email — we'll create a reset link.</p>
      </div>

      {!sent ? (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-xs uppercase tracking-widest text-neutral-500">Email</span>
            <input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none focus:border-violet-500/50" />
          </label>
          <button data-testid="forgot-submit-btn" disabled={loading} className="btn-pill btn-primary w-full py-3.5">
            {loading ? "Sending…" : "Send reset link"}
          </button>
        </form>
      ) : (
        <div className="mt-8 space-y-4">
          <div data-testid="forgot-sent-message" className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200">
            If an account exists for <strong>{email}</strong>, a reset link was created.
          </div>
          {previewToken && (
            <div className="rounded-2xl border border-violet-500/30 bg-violet-500/10 p-4 text-sm text-violet-100">
              <div className="text-xs uppercase tracking-widest text-violet-300 mb-2">Preview mode — reset token</div>
              <code data-testid="forgot-preview-token" className="break-all text-xs">{previewToken}</code>
              <Link
                data-testid="forgot-preview-link"
                to={`/reset-password?token=${previewToken}`}
                className="mt-3 block text-center btn-pill btn-primary py-2"
              >
                Continue to reset password →
              </Link>
            </div>
          )}
          <Link to="/login" data-testid="forgot-back-to-login" className="btn-pill btn-ghost w-full block text-center py-3">
            Back to sign in
          </Link>
        </div>
      )}
    </div>
  );
}
